// Recharts wrapper for an hourly time-series line chart. Themed for the
// Loop dark palette (see lib/theme.tsx). Data is pre-binned server-side;
// the SPA only maps points onto axes.

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  chartPalette,
  chartTheme,
  tokens,
  tooltipContentStyle,
  tooltipItemStyle,
  tooltipLabelStyle,
} from "../lib/theme";

export interface LinePoint {
  ts: string;
  value: number | null;
}

export interface ChartLineProps {
  series: Array<{
    key: string;
    name: string;
    color?: string;
    data: LinePoint[];
  }>;
  height?: number;
  yDomain?: [number | "auto", number | "auto"];
  /** Optional formatter for Y axis ticks + tooltip values (e.g. percentages). */
  format?: (n: number) => string;
}

function mergeSeries(
  series: ChartLineProps["series"],
): Array<Record<string, number | string | null>> {
  const allTs = new Set<string>();
  for (const s of series) {
    for (const p of s.data) allTs.add(p.ts);
  }
  const sortedTs = [...allTs].sort();
  return sortedTs.map((ts) => {
    const row: Record<string, number | string | null> = { ts };
    for (const s of series) {
      const point = s.data.find((p) => p.ts === ts);
      row[s.key] = point ? point.value : null;
    }
    return row;
  });
}

function formatHour(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return `${String(d.getHours()).padStart(2, "0")}:00`;
}

export default function ChartLine({
  series,
  height = 240,
  yDomain,
  format,
}: ChartLineProps) {
  const data = mergeSeries(series);
  const domain: [number | "auto", number | "auto"] = yDomain ?? ["auto", "auto"];
  const fmt = format ?? ((n: number) => n.toFixed(0));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 6" vertical={false} />
        <XAxis
          dataKey="ts"
          tickFormatter={formatHour}
          stroke={chartTheme.axis}
          tick={{ fill: chartTheme.axisText, fontSize: tokens.font.size.xs }}
          tickLine={false}
          axisLine={{ stroke: chartTheme.grid }}
          minTickGap={24}
        />
        <YAxis
          domain={domain}
          stroke={chartTheme.axis}
          tick={{ fill: chartTheme.axisText, fontSize: tokens.font.size.xs }}
          tickFormatter={(n) => (typeof n === "number" ? fmt(n) : String(n))}
          tickLine={false}
          axisLine={false}
          width={48}
        />
        <Tooltip
          contentStyle={tooltipContentStyle}
          labelStyle={tooltipLabelStyle}
          itemStyle={tooltipItemStyle}
          formatter={(v: unknown) =>
            typeof v === "number" ? fmt(v) : v == null ? "—" : String(v)
          }
          labelFormatter={(l) => formatHour(String(l))}
          cursor={{ stroke: tokens.color.tealLight, strokeOpacity: 0.2, strokeWidth: 1 }}
        />
        <Legend
          wrapperStyle={{
            color: tokens.color.fg1,
            fontSize: tokens.font.size.sm,
            paddingTop: tokens.space.sm,
          }}
          iconType="circle"
        />
        {series.map((s, i) => {
          const stroke: string =
            s.color ??
            chartPalette[i % chartPalette.length] ??
            chartPalette[0] ??
            tokens.color.tealLight;
          return (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={stroke}
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 5,
                fill: tokens.color.bg0,
                stroke,
                strokeWidth: 2,
              }}
              connectNulls
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
}
